import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { AlertTriangle, Download, FileText, Scale, Shield, Clock, Users, CheckCircle, ArrowLeft, Gavel, Building2, BookOpen, CreditCard, HelpCircle, Handshake } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const docs = [
  { name: 'Dossier Comercial', file: 'Dossier_Comercial_CERTILEX.pdf', desc: 'Oferta completa, precios y condiciones para particulares y sociedades' },
  { name: 'Gu\u00eda Pr\u00e1ctica: Medidas Cautelar\u00edsimas', file: 'Guia_Practica_Cautelarisimas_CERTILEX.pdf', desc: 'C\u00f3mo solicitar protecci\u00f3n judicial urgente (Art. 135 LJCA)' },
  { name: 'Propuesta para Sociedades Profesionales', file: 'Propuesta_Sociedades_CERTILEX.pdf', desc: 'Modelo de colaboraci\u00f3n institucional con tarifa preferencial' },
  { name: 'Tr\u00e1mite Online: Acta Notarial', file: 'Tramite_Online_Acta_Notarial_CERTILEX.pdf', desc: 'Proceso de acreditaci\u00f3n paso a paso, 100% online' },
];

const TABS = [
  { id: 'contexto', label: 'Contexto', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 'acta', label: 'Acta Notarial', icon: <Scale className="w-4 h-4" /> },
  { id: 'tramitacion', label: 'Tramitaci\u00f3n', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'precios', label: 'Precios', icon: <CreditCard className="w-4 h-4" /> },
  { id: 'cautelarisimas', label: 'Cautelar\u00edsimas', icon: <Gavel className="w-4 h-4" />, urgent: true },
  { id: 'sociedades', label: 'Sociedades', icon: <Handshake className="w-4 h-4" /> },
  { id: 'faq', label: 'FAQ', icon: <HelpCircle className="w-4 h-4" /> },
  { id: 'documentos', label: 'Documentos', icon: <Download className="w-4 h-4" /> },
];

const CirugiaEstetica = () => {
  const [activeTab, setActiveTab] = useState('contexto');

  return (
    <div className="certilex-page">
      {/* Header */}
      <header className="cl-header" style={{ background: 'rgba(11,29,58,0.97)' }} data-testid="ce-header">
        <div className="cl-header-inner">
          <a href="/" className="cl-logo" data-testid="ce-logo">CERTILEX<sup>&reg;</sup></a>
          <div className="cl-header-actions">
            <a href="/" className="cl-btn-ghost"><ArrowLeft className="w-4 h-4 inline mr-1" style={{ verticalAlign: 'middle' }} />Inicio</a>
            <a href="/register" className="cl-btn-gold" data-testid="ce-register-header">Registro Gratuito</a>
          </div>
        </div>
      </header>

      {/* Hero compact */}
      <section className="ce-hero-compact" data-testid="ce-hero">
        <div className="ce-hero-compact-inner">
          <div className="ce-hero-badge"><AlertTriangle className="w-5 h-5" /><span>Tramitaci&oacute;n urgente &mdash; Entrada en vigor: 1 de julio de 2026</span></div>
          <h1 className="ce-hero-compact-title" data-testid="ce-hero-title">Protege tu ejercicio profesional</h1>
          <p className="ce-hero-compact-sub">Real Decreto 1277/2003 &mdash; Modificaci&oacute;n 2026: lo que todo cirujano est&eacute;tico debe saber</p>
        </div>
      </section>

      {/* Tabs */}
      <nav className="cl-tabs" data-testid="ce-section-nav">
        <div className="cl-tabs-inner">
          {TABS.map(({ id, label, icon, urgent }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`cl-tab${activeTab === id ? ' cl-tab--active' : ''}${urgent ? ' cl-tab--urgent' : ''}`}
              data-testid={`ce-tab-${id}`}
            >
              {icon}<span>{label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Content */}
      <main className="cl-content" data-testid="ce-content">

        {/* === CONTEXTO === */}
        {activeTab === 'contexto' && (
          <div className="cl-panel" data-testid="ce-panel-contexto">
            <h2 className="cl-panel-title">El contexto regulatorio: por qu&eacute; actuar ahora</h2>
            <p className="cl-panel-text">La reciente modificaci&oacute;n del <strong>Real Decreto 1277/2003</strong>, reforzada por la <strong>Orden SND/1002/2024</strong> y el nuevo texto aprobado en 2026, introduce requisitos de especialidad formal para la direcci&oacute;n de unidades asistenciales de cirug&iacute;a est&eacute;tica <strong>(unidad U.47)</strong>.</p>
            <p className="cl-panel-text">Con <strong>entrada en vigor prevista el 1 de julio de 2026</strong>, miles de m&eacute;dicos legalmente habilitados, con formaci&oacute;n espec&iacute;fica y a&ntilde;os de pr&aacute;ctica cl&iacute;nica consolidada, pueden ver restringido o impedido su ejercicio profesional.</p>

            <h3 className="cl-panel-subtitle" style={{marginTop:'2rem'}}>Impacto pr&aacute;ctico de la norma</h3>
            <div className="ce-impact-grid">
              {[
                { icon: <Building2 className="w-6 h-6" />, title: 'Cierre de unidades', text: 'Cierre de unidades asistenciales operativas sin evaluaci\u00f3n individual de competencia.' },
                { icon: <Users className="w-6 h-6" />, title: 'P\u00e9rdida de actividad', text: 'P\u00e9rdida de actividad profesional de m\u00e9dicos con experiencia acreditada.' },
                { icon: <Scale className="w-6 h-6" />, title: 'Inseguridad jur\u00eddica', text: 'Inseguridad jur\u00eddica para profesionales que ejercen sin t\u00edtulo oficial de especialista MIR.' },
                { icon: <AlertTriangle className="w-6 h-6" />, title: 'Reducci\u00f3n asistencial', text: 'Posible reducci\u00f3n de la oferta asistencial cualificada y aumento de listas de espera.' },
              ].map((item, i) => (
                <div key={i} className="ce-impact-card">
                  <div className="ce-impact-icon">{item.icon}</div>
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </div>
              ))}
            </div>
            <div className="ce-callout" style={{marginTop:'2rem'}}>
              <Shield className="w-6 h-6 flex-shrink-0" />
              <p>Disponer de documentaci&oacute;n estructurada, validada notarialmente e incorporada al expediente colegial no es solo una ventaja: es una <strong>herramienta jur&iacute;dica esencial</strong> para proteger el ejercicio profesional.</p>
            </div>
          </div>
        )}

        {/* === ACTA NOTARIAL === */}
        {activeTab === 'acta' && (
          <div className="cl-panel cl-panel--dark" data-testid="ce-panel-acta">
            <h2 className="cl-panel-title cl-panel-title--light">El acta notarial de notoriedad profesional</h2>
            <p className="cl-panel-subtitle-text cl-panel-subtitle-text--light">Instrumento p&uacute;blico de m&aacute;xima eficacia probatoria en el ordenamiento jur&iacute;dico espa&ntilde;ol</p>
            <div className="ce-two-col" style={{marginTop:'1.5rem'}}>
              <div>
                <p className="cl-panel-text cl-panel-text--light">Es un documento p&uacute;blico autorizado por notario, conforme a la legislaci&oacute;n notarial espa&ntilde;ola, mediante el cual el fedatario p&uacute;blico declara la notoriedad &mdash; la evidencia objetiva y verificable &mdash; de una trayectoria profesional, formaci&oacute;n y experiencia acreditadas.</p>
                <div className="ce-features-grid" style={{marginTop:'1.5rem'}}>
                  {['Plena fe p\u00fablica y valor probatorio reforzado.','Ejecutiva ante administraciones, colegios, tribunales y aseguradoras.','Documenta hechos verificados directamente por el notario.','Inalterable e irrefutable en cuanto a fecha y contenido certificado.'].map((t, i) => (
                    <div key={i} className="ce-feature-item"><CheckCircle className="w-5 h-5" style={{color:'#C5A55A'}} /><span>{t}</span></div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="ce-subtitle ce-subtitle--light">&iquest;Qu&eacute; acredita el acta?</h3>
                <div className="ce-acredita-list">
                  {['Titulaci\u00f3n m\u00e9dica habilitante.','Formaci\u00f3n espec\u00edfica en el \u00e1mbito de actividad.','A\u00f1os de ejercicio profesional y volumen de actividad cl\u00ednica.','Certificaciones, diplomas y cursos especializados.','Ausencia de sanciones colegiales o disciplinarias.','Cobertura de seguro de responsabilidad civil profesional.'].map((t, i) => (
                    <div key={i} className="ce-acredita-item"><CheckCircle className="w-4 h-4" /><span>{t}</span></div>
                  ))}
                </div>
                <h3 className="ce-subtitle ce-subtitle--light" style={{marginTop:'1.5rem'}}>Incorporaci&oacute;n al expediente colegial</h3>
                <p className="cl-panel-text cl-panel-text--light" style={{fontSize:'0.95rem'}}>La Asesor&iacute;a Jur&iacute;dica del <strong style={{color:'#C5A55A'}}>ICOMEM</strong> acept&oacute; formalmente por escrito la incorporaci&oacute;n al expediente colegial, tras la argumentaci&oacute;n jur&iacute;dica de CERTILEX basada en la Ley 2/1974 sobre Colegios Profesionales. Este precedente valida el procedimiento ante el escrutinio jur&iacute;dico m&aacute;s exigente.</p>
              </div>
            </div>
          </div>
        )}

        {/* === TRAMITACIÓN === */}
        {activeTab === 'tramitacion' && (
          <div className="cl-panel" data-testid="ce-panel-tramitacion">
            <h2 className="cl-panel-title">Tramitaci&oacute;n 100% Online</h2>
            <p className="cl-panel-subtitle-text">Sin desplazamientos. Desde cualquier lugar de Espa&ntilde;a o del extranjero. Incluyendo videollamada con notario.</p>
            <div className="ce-steps">
              {[
                { n:'1', t:'Registro en plataforma', d:'Complete el formulario indicando si act\u00faa como particular o en representaci\u00f3n de una sociedad profesional.' },
                { n:'2', t:'Instrucciones personalizadas', d:'CERTILEX contacta y env\u00eda las instrucciones detalladas para la subida de documentaci\u00f3n.' },
                { n:'3', t:'Subida de documentaci\u00f3n', d:'Suba t\u00edtulos, certificados, CV y experiencia a trav\u00e9s de la plataforma segura.' },
                { n:'4', t:'Verificaci\u00f3n t\u00e9cnica', d:'El equipo CERTILEX verifica y estructura el expediente completo.' },
                { n:'5', t:'Videollamada con notario', d:'Sesi\u00f3n de diligencia de notoriedad con el notario colaborador \u2014 sin desplazamiento.' },
                { n:'6', t:'Emisi\u00f3n del acta notarial', d:'El notario emite el acta de notoriedad profesional con plena validez jur\u00eddica.' },
                { n:'7', t:'Entrega del documento', d:'Reciba el acta en formato digital certificado y copia f\u00edsica.' },
                { n:'8', t:'Expediente colegial', d:'CERTILEX gestiona la incorporaci\u00f3n al Colegio de M\u00e9dicos correspondiente.' },
                { n:'9', t:'Alta en registro CERTILEX', d:'Registro centralizado con el nivel de visibilidad que usted elija (p\u00fablico o privado).' },
              ].map((s, i) => (
                <div key={i} className="ce-step">
                  <div className="ce-step-num">{s.n}</div>
                  <div className="ce-step-body">
                    <h4 className="ce-step-title">{s.t}</h4>
                    <p className="ce-step-desc">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* === PRECIOS === */}
        {activeTab === 'precios' && (
          <div className="cl-panel" data-testid="ce-panel-precios">
            <h2 className="cl-panel-title">Oferta econ&oacute;mica</h2>
            <p className="cl-panel-subtitle-text">Todos los precios incluyen: tramitaci&oacute;n completa, acta notarial, coordinaci&oacute;n colegial, alta en registro y mantenimiento 12 meses.</p>
            <div className="ce-pricing-grid">
              <div className="ce-price-card ce-price-card--urgent">
                <div className="ce-price-badge">URGENTE</div>
                <h3>Resoluci&oacute;n en 7 d&iacute;as</h3>
                <div className="ce-price">1.800 &euro;</div>
                <p className="ce-price-sub">Por profesional</p>
                <ul>
                  <li><CheckCircle className="w-4 h-4" /><span>Verificaci&oacute;n documental acelerada</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Turno preferente en notar&iacute;a</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Acta notarial + expediente colegial</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Ideal si quedan menos de 30 d&iacute;as</span></li>
                </ul>
              </div>
              <div className="ce-price-card">
                <h3>Colegiado particular</h3>
                <div className="ce-price">1.500 &euro;</div>
                <p className="ce-price-sub">Est&aacute;ndar &mdash; 10 a 20 d&iacute;as h&aacute;biles</p>
                <ul>
                  <li><CheckCircle className="w-4 h-4" /><span>Tramitaci&oacute;n completa</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Acta notarial + expediente colegial</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Alta en registro CERTILEX 12 meses</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Factura desgravable</span></li>
                </ul>
              </div>
              <div className="ce-price-card ce-price-card--social">
                <div className="ce-price-badge ce-price-badge--social">25% DTO.</div>
                <h3>Grupo / Sociedad</h3>
                <div className="ce-price">1.125 &euro;<span className="ce-price-unit">/prof.</span></div>
                <p className="ce-price-sub">A partir de 20 suscripciones</p>
                <ul>
                  <li><CheckCircle className="w-4 h-4" /><span>Todo lo incluido en est&aacute;ndar</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Registro colectivo de la sociedad</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Informes t&eacute;cnicos colectivos</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Grupos &gt;50: precio especial</span></li>
                </ul>
              </div>
            </div>
            <div className="ce-callout" style={{marginTop:'2rem'}}>
              <Clock className="w-5 h-5 flex-shrink-0" />
              <div>
                <p><strong>Inscripci&oacute;n inmediata sin esperar decisiones colectivas:</strong></p>
                <p style={{marginTop:'0.5rem'}}>Inscr&iacute;base hoy con tarifa individual (1.500&nbsp;&euro;). Si su sociedad contrata despu&eacute;s con 20+ miembros, CERTILEX le devuelve autom&aacute;ticamente la diferencia (<strong>375&nbsp;&euro;</strong>) en 10 d&iacute;as h&aacute;biles.</p>
              </div>
            </div>
            <h3 className="cl-panel-subtitle" style={{marginTop:'2.5rem'}}>Forma de pago</h3>
            <div className="ce-payment-grid">
              <div className="ce-payment-card"><h4>Particulares</h4><p>Tarjeta bancaria (enlace seguro por correo) o transferencia bancaria. Factura con datos fiscales, desgravable como gasto profesional.</p></div>
              <div className="ce-payment-card"><h4>Grupos y sociedades</h4><p>Exclusivamente transferencia bancaria. Factura pro forma previa al inicio del proceso. Descuento del 25% aplicado autom&aacute;ticamente a partir de 20 profesionales.</p></div>
            </div>
          </div>
        )}

        {/* === CAUTELARÍSIMAS === */}
        {activeTab === 'cautelarisimas' && (
          <div className="cl-panel cl-panel--dark" data-testid="ce-panel-cautelarisimas">
            <h2 className="cl-panel-title cl-panel-title--light"><Gavel className="w-7 h-7 inline mr-2" style={{color:'#C5A55A', verticalAlign:'middle'}} />Medidas cautelar&iacute;simas &mdash; Gu&iacute;a pr&aacute;ctica</h2>
            <p className="cl-panel-subtitle-text cl-panel-subtitle-text--light">Art. 135 LJCA &mdash; El mecanismo procesal de m&aacute;xima urgencia del contencioso-administrativo espa&ntilde;ol</p>
            <div className="ce-essentials-grid">
              {[
                { icon:<Scale className="w-8 h-8" />, t:'\u00bfSe necesita abogado?', d:'S\u00ed, obligatoriamente. Especializado en derecho administrativo sanitario.' },
                { icon:<Users className="w-8 h-8" />, t:'\u00bfIndividual o colectivo?', d:'Colectivo es claramente superior: mayor fuerza, menor coste por profesional, m\u00e1s impacto.' },
                { icon:<Building2 className="w-8 h-8" />, t:'\u00bfD\u00f3nde se presenta?', d:'Audiencia Nacional (norma estatal) o TSJ de la comunidad aut\u00f3noma.' },
                { icon:<Clock className="w-8 h-8" />, t:'\u00bfCu\u00e1ndo decide el juez?', d:'En 24\u201372 horas, sin audiencia previa de la Administraci\u00f3n.' },
              ].map((c,i) => (
                <div key={i} className="ce-essential-card"><div>{c.icon}</div><h4>{c.t}</h4><p>{c.d}</p></div>
              ))}
            </div>
            <h3 className="ce-subtitle ce-subtitle--light" style={{marginTop:'2.5rem', textAlign:'center'}}>Comparativa: Individual vs. Colectivo</h3>
            <div className="ce-table-wrap">
              <table className="ce-table" data-testid="ce-comparison-table">
                <thead><tr><th>Factor</th><th>Individual</th><th>Colectivo</th></tr></thead>
                <tbody>
                  <tr><td>Coste total de honorarios</td><td>Alto (3.800&ndash;7.500 &euro;)</td><td>Compartido (200&ndash;2.000 &euro;/prof.)</td></tr>
                  <tr><td>Fuerza probatoria</td><td>Limitada a un caso</td><td>Impacto colectivo y sist&eacute;mico</td></tr>
                  <tr><td>Impacto sobre el tribunal</td><td>Un profesional</td><td>Decenas o cientos &rarr; mayor peso</td></tr>
                  <tr><td>Visibilidad medi&aacute;tica</td><td>Nula</td><td>Alta &rarr; presi&oacute;n institucional</td></tr>
                  <tr><td>Efecto sobre la norma</td><td>Solo suspensi&oacute;n individual</td><td>Puede suspender para todo el colectivo</td></tr>
                </tbody>
              </table>
            </div>
            <h3 className="ce-subtitle ce-subtitle--light" style={{marginTop:'2.5rem', textAlign:'center'}}>Coste estimado total por profesional</h3>
            <div className="ce-table-wrap">
              <table className="ce-table" data-testid="ce-cost-table">
                <thead><tr><th>Concepto</th><th>20 prof.</th><th>50 prof.</th><th>100 prof.</th><th>Individual</th></tr></thead>
                <tbody>
                  <tr><td>Honorarios legales</td><td>1.000&ndash;2.000 &euro;</td><td>400&ndash;800 &euro;</td><td>200&ndash;400 &euro;</td><td>3.800&ndash;7.500 &euro;</td></tr>
                  <tr><td>Acta CERTILEX est&aacute;ndar</td><td>1.125 &euro;</td><td>1.125 &euro;</td><td>1.125 &euro;</td><td>1.500 &euro;</td></tr>
                  <tr className="ce-table-highlight"><td><strong>TOTAL</strong></td><td><strong>2.125&ndash;3.125 &euro;</strong></td><td><strong>1.525&ndash;1.925 &euro;</strong></td><td><strong>1.325&ndash;1.525 &euro;</strong></td><td><strong>5.300&ndash;9.000 &euro;</strong></td></tr>
                </tbody>
              </table>
            </div>
            <div className="ce-callout ce-callout--dark" style={{marginTop:'2rem'}}>
              <FileText className="w-5 h-5 flex-shrink-0" />
              <p><strong>El papel del acta CERTILEX en el recurso:</strong> Acredita el <em>fumus boni iuris</em> (apariencia de buen derecho) y el <em>periculum in mora</em> (perjuicio irreparable) con m&aacute;xima solidez probatoria.</p>
            </div>
          </div>
        )}

        {/* === SOCIEDADES === */}
        {activeTab === 'sociedades' && (
          <div className="cl-panel" data-testid="ce-panel-sociedades">
            <h2 className="cl-panel-title">Propuesta para Sociedades Profesionales</h2>
            <p className="cl-panel-subtitle-text">Modelo de colaboraci&oacute;n institucional para ofrecer a sus miembros una herramienta de acreditaci&oacute;n homog&eacute;nea, coordinada y con respaldo notarial.</p>
            <div className="ce-two-col ce-two-col--light">
              <div>
                <h3 className="ce-subtitle">Modelo operativo</h3>
                <div className="ce-soc-steps">
                  {['La sociedad suscribe un acuerdo marco de colaboraci\u00f3n con CERTILEX.','CERTILEX proporciona una plataforma dedicada para los miembros.','Los miembros tramitan su acreditaci\u00f3n a tarifa preferencial (25% dto.).','La sociedad dispone de un registro colectivo de profesionales acreditados.','CERTILEX emite informes t\u00e9cnicos colectivos para uso institucional o judicial.'].map((t,i) => (
                    <div key={i} className="ce-soc-step"><span className="ce-soc-step-num">{i+1}</span><span>{t}</span></div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="ce-subtitle">Beneficios para la sociedad</h3>
                <div className="ce-soc-benefits">
                  {['Herramienta de defensa colectiva documentada ante restricciones regulatorias.','Posicionamiento institucional basado en competencia profesional real.','Registro homog\u00e9neo de socios con formaci\u00f3n y experiencia acreditadas.','Mayor credibilidad t\u00e9cnica ante la Administraci\u00f3n y los tribunales.','Oferta de valor diferencial para captaci\u00f3n y fidelizaci\u00f3n de miembros.'].map((t,i) => (
                    <div key={i} className="ce-soc-item"><CheckCircle className="w-5 h-5" style={{color:'#C5A55A'}} /><span>{t}</span></div>
                  ))}
                </div>
              </div>
            </div>
            <h3 className="cl-panel-subtitle" style={{marginTop:'2.5rem'}}>Valor estrat&eacute;gico del expediente CERTILEX</h3>
            <div className="ce-strategy-list ce-strategy-list--light" style={{maxWidth:'100%'}}>
              {['Acreditar competencia profesional en solicitudes de autorizaci\u00f3n sanitaria.','Apoyar la apariencia de buen derecho en recursos contencioso-administrativos.','Aportar documentaci\u00f3n en solicitudes de medidas cautelar\u00edsimas (art. 135 LJCA).','Negociar reg\u00edmenes transitorios con la Administraci\u00f3n sanitaria.','Reforzar la posici\u00f3n ante aseguradoras en la contrataci\u00f3n de responsabilidad civil.','Fundamentar reclamaciones individuales o colectivas de acreditaci\u00f3n profesional.'].map((t,i) => (
                <div key={i} className="ce-strategy-item ce-strategy-item--light"><Shield className="w-5 h-5" style={{color:'#C5A55A'}} /><span>{t}</span></div>
              ))}
            </div>
          </div>
        )}

        {/* === FAQ === */}
        {activeTab === 'faq' && (
          <div className="cl-panel" data-testid="ce-panel-faq">
            <h2 className="cl-panel-title">Preguntas frecuentes</h2>
            <div className="cl-panel-center">
              <Accordion type="single" collapsible className="cl-faq-list" style={{width:'100%'}}>
                {[
                  { q:'\u00bfCERTILEX emite certificados?', a:'No. CERTILEX gestiona y estructura expedientes. Las certificaciones son emitidas por los notarios colaboradores con plena validez jur\u00eddica.' },
                  { q:'\u00bfEl ICOMEM acepta la incorporaci\u00f3n al expediente colegial?', a:'S\u00ed. La Asesor\u00eda Jur\u00eddica del ICOMEM acept\u00f3 formalmente por escrito la incorporaci\u00f3n, tras la argumentaci\u00f3n jur\u00eddica de CERTILEX basada en la Ley 2/1974 sobre Colegios Profesionales. Los mismos argumentos jur\u00eddicos que fundamentaron esta aceptaci\u00f3n en el ICOMEM son de aplicaci\u00f3n general y deben servir igualmente ante cualquier otro Colegio Oficial de M\u00e9dicos del territorio nacional.' },
                  { q:'\u00bfPuedo inscribirme sin esperar a mi sociedad?', a:'S\u00ed. Puede inscribirse individualmente de inmediato. Si su sociedad contrata despu\u00e9s, CERTILEX le devuelve autom\u00e1ticamente la diferencia (375\u00a0\u20ac) en 10 d\u00edas h\u00e1biles.' },
                  { q:'\u00bfQu\u00e9 plazo tengo?', a:'La entrada en vigor est\u00e1 prevista para el 1 de julio de 2026. Se recomienda iniciar el tr\u00e1mite con suficiente antelaci\u00f3n. La modalidad URGENTE resuelve en 7 d\u00edas h\u00e1biles.' },
                  { q:'\u00bfSe puede tramitar desde el extranjero?', a:'S\u00ed. Toda la tramitaci\u00f3n es 100% online, incluyendo la videollamada con el notario.' },
                  { q:'\u00bfQu\u00e9 diferencia hay entre el acta notarial y un certificado privado?', a:'Un certificado privado tiene eficacia limitada. El acta notarial es un documento p\u00fablico: el notario verifica personalmente la documentaci\u00f3n y certifica ante el Estado la realidad de los hechos. Esa diferencia es determinante en procedimientos judiciales y administrativos.' },
                  { q:'\u00bfCERTILEX facilita contacto con abogados especializados?', a:'S\u00ed, exclusivamente para miembros certificados que ya dispongan de su acta notarial emitida. CERTILEX informa sobre despachos especializados en derecho administrativo sanitario, previa solicitud expresa del interesado.' },
                ].map((item,i) => (
                  <AccordionItem key={i} value={`q${i}`} className="cl-faq-item">
                    <AccordionTrigger className="cl-faq-trigger">{item.q}</AccordionTrigger>
                    <AccordionContent className="cl-faq-content">{item.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        )}

        {/* === DOCUMENTOS === */}
        {activeTab === 'documentos' && (
          <div className="cl-panel cl-panel--dark" data-testid="ce-panel-documentos">
            <h2 className="cl-panel-title cl-panel-title--light"><Download className="w-7 h-7 inline mr-2" style={{color:'#C5A55A', verticalAlign:'middle'}} />Documentaci&oacute;n descargable</h2>
            <p className="cl-panel-subtitle-text cl-panel-subtitle-text--light">Toda la informaci&oacute;n detallada en PDF para su consulta y distribuci&oacute;n.</p>
            <div className="ce-docs-grid">
              {docs.map((doc, i) => (
                <a key={i} href={`${BACKEND_URL}/api/static/docs/${doc.file}`} target="_blank" rel="noopener noreferrer" className="ce-doc-card" data-testid={`download-doc-${i}`}>
                  <FileText className="w-8 h-8 flex-shrink-0" style={{color:'#C5A55A'}} />
                  <div className="ce-doc-info"><h4>{doc.name}</h4><p>{doc.desc}</p></div>
                  <Download className="w-5 h-5 ce-doc-dl flex-shrink-0" />
                </a>
              ))}
            </div>
            <div style={{textAlign:'center', marginTop:'2.5rem'}}>
              <a href="/register" className="cl-btn-cta" style={{display:'inline-flex', alignItems:'center', padding:'0.75rem 2.5rem', textDecoration:'none', borderRadius:'4px'}} data-testid="ce-final-register">Registro Gratuito</a>
              <p style={{marginTop:'1rem', color:'rgba(255,255,255,0.5)', fontSize:'0.9rem'}}>Tramitaci&oacute;n urgente disponible &mdash; Plazo: antes del 1 de julio de 2026</p>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="cl-footer" data-testid="ce-footer">
        <div className="cl-container">
          <div className="cl-footer-main">
            <div className="cl-footer-brand">
              <h3 className="cl-footer-logo">CERTILEX<sup>&reg;</sup></h3>
              <p className="cl-footer-desc">Certificaci&oacute;n Legal y Validaci&oacute;n Profesional Sanitaria</p>
            </div>
            <div className="cl-footer-dimps">
              <p>El proyecto Certilex&reg; parte de nuestra American Limited Liability Company <strong>DIMPS</strong>. Contamos con un equipo de profesionales de primer nivel en la abogac&iacute;a, notar&iacute;a y asuntos p&uacute;blicos y regulatorios. Nuestro objetivo: que la trayectoria profesional de nuestros clientes se proyecte al m&aacute;ximo nivel de sus competencias.</p>
            </div>
          </div>
          <div className="cl-footer-bottom">
            <p>Contacto: <a href="mailto:info@certilex.eu">info@certilex.eu</a></p>
            <p>&copy; {new Date().getFullYear()} CERTILEX. Todos los derechos reservados.</p>
            <p className="cl-footer-credit">Dise&ntilde;o: <strong>DIMPS</strong> &mdash; Servicios y Marketing Digitales</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CirugiaEstetica;
